import {
  Button,
  ButtonSizeType,
  ButtonVariantType,
  IconName,
} from '@design-system';
import { useTranslation } from 'next-i18next';
import React, { useContext, useEffect } from 'react';

import { AuthEntryPoint } from '../../../features/auth/types';
import { useIsSubscribed } from '../../../hooks/subscription';
import { useAuthenticate } from '../../../hooks/useAuthenticate';
import { useAuth } from '../../../providers';
import { AccountContext } from '../../../providers/AccountProvider';
import type { ProductStatus } from '../../../types/Analytics';
import type { ButtonLocation } from '../../../types/Subscriptions';
import type { NFTProductType } from '../../../types/web3/product';
import { renderAugmentedArtistsNames } from '../../../utils/artists';
import { storyblokProductReleaseType } from '../../../utils/storyblokReleaseType';
import { buildSubscriptions } from '../../../utils/subscriptions';
import { showPhoneStep } from '../../../helpers/phone';

type Props = {
  buttonLocation?: ButtonLocation;
  disabled?: boolean;
  fullWidth?: boolean;
  isPreOrder?: boolean;
  isSoldOut?: boolean;
  labels: {
    normal: string;
    success: string;
  };
  onClick?: () => void;
  onClickSkipSubscription?: boolean;
  product: ProductType | NFTProductType;
  productRef: string;
  productStatus: ProductStatus;
  icon?: {
    normal?: IconName;
    success?: IconName;
  };
  showIcon?: boolean;
  size?: ButtonSizeType;
  variant?: ButtonVariantType;
};

export const SignUpButton: React.FC<Props> = ({
  buttonLocation = 'product page',
  disabled = false,
  fullWidth = true,
  icon = {
    normal: 'ic_notification_outline',
    success: 'ic_notification_active',
  },
  isPreOrder = false,
  isSoldOut = false,
  labels,
  onClick,
  onClickSkipSubscription = false,
  product,
  productRef,
  productStatus,
  showIcon = false,
  size = 'lg',
  variant = 'primary',
}) => {
  const { t } = useTranslation('authentication');
  const { user } = useAuth();
  const { authenticateAndSubscribe } = useAuthenticate();
  const productId = !('tokenId' in product) ? product.id[0]?.id : '';
  const { onboarding } = useContext(AccountContext);
  const phoneStep = showPhoneStep(user) && productStatus === 'coming soon';

  const artists = product.productArtist?.map(
    (a: ResolvedRelatedArtist) => a.artist,
  );

  const subscriptions = buildSubscriptions({
    includeGeneral: true,
    artists,
    productId,
    productRef,
    productTitle: product.title,
    isPreOrder,
  });

  const { isSubscribedAll, loading, refetch } = useIsSubscribed(
    subscriptions.map(({ objectId, type }) => ({ objectId, type })),
  );

  useEffect(() => {
    if (user) refetch();
  }, [user]);

  const authScreenThumbnail = Array.isArray(product.thumbnail)
    ? product.thumbnail[0]?.filename
    : product.thumbnail?.filename;

  const authScreenTitle = isSoldOut
    ? t('dynamicTitle.waitingList')
    : t('dynamicTitle.product');

  return (
    <Button
      disabled={isSubscribedAll || disabled}
      fullWidth={fullWidth}
      icon={
        showIcon ? (isSubscribedAll ? icon?.success : icon?.normal) : undefined
      }
      label={isSubscribedAll ? labels.success : labels.normal}
      loading={loading}
      onClick={async () => {
        onClick?.();
        if (onClickSkipSubscription) {
          return;
        }
        await authenticateAndSubscribe({
          subscriptions,
          authScreenThumbnail,
          authScreenTitle,
          analyticsProperties: {
            'artist name': renderAugmentedArtistsNames(artists),
            'button_location': buttonLocation,
            'product_id': productId,
            'product_release_type': storyblokProductReleaseType(product),
            'product_status': productStatus,
            'product_title': product.title,
            'source': isPreOrder
              ? AuthEntryPoint.PreOrder
              : AuthEntryPoint.Product,
          },
          showPhoneStep: phoneStep,
          showOnboarding: !phoneStep,
          hideSuccessScreen: !onboarding?.isOnboardingStarted,
          onFlowComplete: () => {
            onboarding?.show({ page: AuthEntryPoint.Product, id: productId });
          },
        });
        refetch();
      }}
      size={size}
      variant={isSubscribedAll ? 'positive' : variant}
    />
  );
};
